import { useEffect, useState, useRef } from "react";
import { FormControl, FormLabel } from "@mui/material";
import { Editor as TinyMceEditor } from "@tinymce/tinymce-react";

let imgSrcs = []; // global imageSrc variable to save original img src attributes
const wrapImages = (html, images) => {
  imgSrcs = [];
  let i = 0;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const imgs = doc.querySelectorAll("img");
  for (let img of imgs) {
    const origSrc = img.src;
    // replace images starting with cid: and replace them with data urls
    if (origSrc.match(/^cid:/)) {
      imgSrcs[i++] = origSrc;
      const name = origSrc.replace(/^cid:/, ""); // remove cid: at beginning
      const imgData = images.find((x) => JSON.stringify(x.name) === JSON.stringify(name));
      if (imgData) {
        img.setAttribute("data", img.src);
        img.setAttribute("src", imgData.url);
      }
    }
  }

  return doc.documentElement.innerHTML;
};

const unWrapImages = (html) => {
  let i = 0;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const figs = doc.querySelectorAll("img");
  for (let fig of figs) {
    fig.src = imgSrcs[i++];
  }

  return doc.documentElement.innerHTML;
};

// "Insert/edit image" dialog -> let the user pick a local file and embed it as a base64 data url
const filePickerCallback = (cb) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = () => {
    const file = input.files[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => cb(reader.result, { title: file.name });
    reader.readAsDataURL(file);
  };
  input.click();
};

// keep pasted/inserted images inline as base64 instead of uploading them to a server
const imagesUploadHandler = (blobInfo) => Promise.resolve(`data:${blobInfo.blob().type};base64,${blobInfo.base64()}`);

/**
 * @param {object} props
 * @param {boolean} [props.allowImages] When true, the editor lets the user insert/paste images that
 *   are stored inline as base64 data urls. This is mutually exclusive with the `images` (cid:) prop.
 * @param {Array<{name: string, url: string}>} [props.images] cid: image map used for email attachments.
 */
const HtmlEditor = ({ label, htmlContent, images = [], onBlur, disabled, allowImages = false, allowLinks = false }) => {
  if (allowImages && images.length > 0) {
    throw new Error("HtmlEditor: `allowImages` cannot be combined with the `images` (cid:) prop - pick one pattern.");
  }

  const [editorHasFocus, setEditorHasFocus] = useState(false);
  const [contentWithImages, setContentWithImages] = useState("");
  const editorRef = useRef();

  useEffect(() => {
    setContentWithImages(allowImages ? htmlContent : wrapImages(htmlContent, images));
  }, [htmlContent, images, allowImages]);

  const editorBlur = (evt) => {
    setEditorHasFocus(false);
    let content = evt.target.getContent();
    if (!allowImages) {
      content = unWrapImages(content);
    }
    //console.log("email content", content);
    onBlur(content);
  };

  const editorFocus = () => {
    setEditorHasFocus(true);
  };

  const plugins = ["advlist", "autolink", "lists", "link", "image", "anchor", "visualblocks", "media", "table"];
  const toolbarImageLink =
    allowImages || allowLinks
      ? [" |", allowLinks ? "link" : null, allowImages ? "image" : null].filter((s) => !!s).join(" ")
      : "";
  const toolbar = `undo redo | blocks | styleselect | hr | bold italic underline${toolbarImageLink} | bullist numlist `;

  return (
    <>
      {/* this FormControl type"fieldset" will provide the border and the label for the editor */}
      <FormControl
        component="fieldset"
        sx={{
          width: "100%", // maybe need to be changed
          padding: 1,
          borderColor: (theme) => (editorHasFocus ? theme.palette.info.main : theme.palette.grey[400]) + " !important",
          "&:hover": {
            borderColor: (theme) => (editorHasFocus ? theme.palette.info.main : "black") + " !important",
          },
          border: editorHasFocus ? 2 : 1,
          borderRadius: 1,
        }}
      >
        {label && (
          <FormLabel
            component="legend"
            sx={{ pl: 1, pr: 1, fontSize: 11, color: (theme) => (editorHasFocus ? theme.palette.info.main : null) }}
          >
            {label}
          </FormLabel>
        )}
        <TinyMceEditor
          tinymceScriptSrc="/static/tinymce/tinymce.min.js"
          disabled={disabled}
          onInit={(evt, editor) => (editorRef.current = editor)}
          initialValue={contentWithImages}
          value={null}
          onBlur={editorBlur}
          onFocus={editorFocus}
          init={{
            height: 500,
            menubar: false,
            plugins,
            toolbar,
            block_formats: "Normal=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Preformatted=pre",
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:14px } img { max-width: 100%; height: auto; }",
            branding: false,
            elementpath: false,
            ...(allowImages
              ? {
                  paste_data_images: true,
                  automatic_uploads: true,
                  file_picker_types: "image",
                  file_picker_callback: filePickerCallback,
                  images_upload_handler: imagesUploadHandler,
                }
              : {}),
          }}
        />
      </FormControl>
    </>
  );
};

export default HtmlEditor;
